import {
  CircleDot,
  KeyRound,
  Network,
  Play,
  RefreshCw,
  SlidersHorizontal,
  type LucideIcon,
} from 'lucide-react';
import { createElement, Fragment, useEffect, useId, useState, type InputHTMLAttributes } from 'react';
import { Link } from 'react-router';
import { useActivity, useNetworkActivity } from '../api/queries';
import type { ActivityEntry } from '../api/types';
import { useAuth } from '../auth/context';
import { ErrorMessage } from '../components/ui';
import { formatDateTime, formatRelative } from '../lib/format';
import { useNetworking } from '../networks/shared';
import { strings } from '../strings';
import {
  ACTIVITY_GROUPS,
  actorLabel,
  describeActivity,
  foldRevealRequests,
  hasActivityFilter,
  NO_ACTIVITY_FILTER,
  toActivityFilter,
  type ActivityContext,
  type ActivityFilterState,
  type ActivityGroup,
  type ActivityPart,
  type ActivityTargets,
} from './describe';

const a = strings.activity;

const GROUP_ICONS: Record<ActivityGroup, LucideIcon> = {
  settings: SlidersHorizontal,
  runs: Play,
  secrets: KeyRound,
  apps: RefreshCw,
  network: Network,
};
const GROUP_TONES: Record<ActivityGroup, string> = {
  settings: 'info',
  runs: 'neutral',
  secrets: 'violet',
  apps: 'success',
  network: 'info',
};

/** A text input that reports its value 300 ms after typing stops (the secret and person filters). */
function DebouncedInput({
  value,
  onCommit,
  ...rest
}: { value: string; onCommit: (value: string) => void } & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
>) {
  const [draft, setDraft] = useState(value);
  const [seen, setSeen] = useState(value);
  if (value !== seen) {
    setSeen(value);
    setDraft(value);
  }
  useEffect(() => {
    if (draft === value) return;
    const timer = setTimeout(() => onCommit(draft), 300);
    return () => clearTimeout(timer);
  }, [draft, value, onCommit]);
  return <input {...rest} value={draft} onChange={(event) => setDraft(event.target.value)} />;
}

/** A target in monospace: a button that shows it when it still exists and the page can show it (6.11.3). */
function PartCode({
  part,
  targets,
}: {
  part: Extract<ActivityPart, { code: string }>;
  targets?: ActivityTargets;
}) {
  const target = part.target;
  const exists =
    !!target && !!targets && (target.kind === 'secret' ? targets.secrets : targets.resources).has(target.id);
  if (!exists) return <code className="act-code">{part.code}</code>;
  return (
    <button
      type="button"
      className="act-code act-target"
      aria-label={a.showTarget(part.code)}
      title={a.showTarget(part.code)}
      onClick={() => targets!.onShow(target!)}
    >
      {part.code}
    </button>
  );
}

/** One entry (spec 6.11.3): icon, actor, sentence, outcome, settings changes, time and the run link. */
export function ActivityItem({
  entry,
  now,
  targets,
  context,
}: {
  entry: ActivityEntry;
  now?: number;
  targets?: ActivityTargets;
  context?: ActivityContext;
}) {
  const description = describeActivity(entry, 10, context);
  const group = description.group;
  return (
    <li className="act-entry" data-testid="activity-entry" data-kind={entry.kind}>
      <span className={`var-icon-tile var-tone-${group ? GROUP_TONES[group] : 'neutral'}`} aria-hidden>
        {createElement(group ? GROUP_ICONS[group] : CircleDot, { className: 'size-3.5' })}
      </span>
      <div className="min-w-0 flex-1">
        <p className="act-sentence">
          <span className="font-semibold">{actorLabel(entry)}</span>{' '}
          {description.parts.map((part, i) => (
            <Fragment key={i}>
              {i > 0 && ' '}
              {'code' in part ? (
                <PartCode part={part} targets={targets} />
              ) : part.to ? (
                <Link className="link" to={part.to}>
                  {part.text}
                </Link>
              ) : (
                part.text
              )}
            </Fragment>
          ))}
          {entry.outcome !== 'succeeded' && ' '}
          {entry.outcome !== 'succeeded' && (
            <span
              className={`badge ${entry.outcome === 'failed' ? 'tone-danger' : entry.outcome === 'pending' ? 'tone-warning' : 'tone-neutral'}`}
            >
              {a.outcome[entry.outcome]}
            </span>
          )}
        </p>
        {description.changes.length > 0 && (
          <ul className="act-changes">
            {description.changes.map((change, i) => (
              <li key={i}>
                <span className="text-fg-muted">{change.label}:</span>{' '}
                <code className="act-code">{change.before}</code> →{' '}
                <code className="act-code">{change.after}</code>
              </li>
            ))}
            {description.more > 0 && <li className="text-fg-muted">{a.moreChanges(description.more)}</li>}
          </ul>
        )}
        <p className="act-meta">
          <time dateTime={entry.at} title={formatDateTime(entry.at)}>
            {formatRelative(entry.at, now)}
          </time>
          {description.runId && (
            <>
              {' · '}
              <Link className="link" to={`/runs/${description.runId}`}>
                {a.openRun}
              </Link>
            </>
          )}
        </p>
      </div>
    </li>
  );
}

/**
 * The deployment's activity timeline (spec 6.11): filters, entries newest first, and Load more. Used by the
 * deployment page's Activity tab and by the Activity window.
 */
export function ActivityTimeline({
  deployment,
  network,
  filter,
  onFilterChange,
  resources,
  compact = false,
  targets,
}: {
  /** The deployment whose activity this shows; or `network`. */
  deployment?: { id: string; name: string };
  /** A network's activity (feature spec 17): the Kind and Person filters only; `deployments` still exist (links). */
  network?: { id: string; name: string; deployments: ReadonlySet<string> };
  filter: ActivityFilterState;
  onFilterChange: (filter: ActivityFilterState) => void;
  /** The resources the Resource filter offers. */
  resources: readonly string[];
  compact?: boolean;
  /** Secrets and apps that still exist, shown as buttons (spec 6.11.3); text without. */
  targets?: ActivityTargets;
}) {
  const { me } = useAuth();
  const ids = useId();
  const networking = useNetworking();
  const deploymentQuery = useActivity(network ? null : (deployment?.id ?? null), toActivityFilter(filter));
  const networkQuery = useNetworkActivity(network?.id ?? null, toActivityFilter(filter));
  const query = network ? networkQuery : deploymentQuery;
  const owner = network ?? deployment ?? { id: '', name: '' };
  const context: ActivityContext | undefined = network ? { network } : undefined;
  // The Network group only while networking is on; a network's timeline offers its own kinds and runs.
  const groups = (Object.keys(ACTIVITY_GROUPS) as ActivityGroup[]).filter((group) =>
    network ? group === 'network' || group === 'runs' : group !== 'network' || networking.enabled,
  );
  const entries = query.data?.pages.flatMap((page) => page.items) ?? [];
  const set = (patch: Partial<ActivityFilterState>) => onFilterChange({ ...filter, ...patch });
  const filtered = hasActivityFilter(filter);
  const mine = !!me.id && filter.actor === me.id;
  const notFound = query.error && (query.error as { status?: number }).status === 404;

  return (
    <div className={`act-timeline ${compact ? 'is-compact' : ''}`}>
      <div role="group" aria-label={a.filters.label} className="act-filters">
        <div className="act-filter">
          <label htmlFor={`${ids}-kind`} className="text-fg-muted">
            {a.filters.kind}
          </label>
          <select
            id={`${ids}-kind`}
            className="input"
            value={filter.kind}
            onChange={(event) => set({ kind: event.target.value })}
          >
            <option value="">{a.filters.allKinds}</option>
            {groups.map((group) => (
              <optgroup key={group} label={a.filters.groups[group]}>
                <option value={`group:${group}`}>{a.filters.allOf(a.filters.groups[group])}</option>
                {ACTIVITY_GROUPS[group].map((kind) => (
                  <option key={kind} value={kind}>
                    {a.kinds[kind]}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        {!network && (
          <>
            <div className="act-filter">
              <label htmlFor={`${ids}-secret`} className="text-fg-muted">
                {a.filters.secret}
              </label>
              <DebouncedInput
                id={`${ids}-secret`}
                className="input font-mono"
                type="search"
                placeholder={a.filters.secretPlaceholder}
                value={filter.secret}
                onCommit={(secret) => set({ secret })}
              />
            </div>
            <div className="act-filter">
              <label htmlFor={`${ids}-resource`} className="text-fg-muted">
                {a.filters.resource}
              </label>
              <select
                id={`${ids}-resource`}
                className="input"
                value={filter.resource}
                onChange={(event) => set({ resource: event.target.value })}
              >
                <option value="">{a.filters.allResources}</option>
                {[...new Set([...resources, ...(filter.resource ? [filter.resource] : [])])].map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
        <div className="act-filter">
          <label htmlFor={`${ids}-person`} className="text-fg-muted">
            {a.filters.person}
          </label>
          <DebouncedInput
            id={`${ids}-person`}
            className="input"
            type="search"
            placeholder={a.filters.personPlaceholder}
            value={filter.actor}
            onCommit={(actor) => set({ actor })}
          />
        </div>
        {me.id && (
          <button
            type="button"
            className="btn btn-sm"
            aria-pressed={mine}
            onClick={() => set({ actor: mine ? '' : me.id! })}
          >
            {a.filters.onlyMine}
          </button>
        )}
        {filtered && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onFilterChange(NO_ACTIVITY_FILTER)}
          >
            {a.clearFilters}
          </button>
        )}
      </div>
      {query.error && !notFound ? (
        <ErrorMessage error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending && !notFound ? (
        <div className="act-skeleton" role="status" aria-busy="true">
          <span className="sr-only">{strings.common.loading}</span>
          {[0, 1, 2].map((i) => (
            <div key={i} className="var-skeleton-row" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <div className="act-empty">
          <p>{filtered ? a.noMatch : a.empty}</p>
          {filtered && (
            <button
              type="button"
              className="btn btn-sm mt-2"
              onClick={() => onFilterChange(NO_ACTIVITY_FILTER)}
            >
              {a.clearFilters}
            </button>
          )}
        </div>
      ) : (
        <>
          <ol aria-label={a.listLabel(owner.name)} className="act-list">
            {foldRevealRequests(entries).map((entry) => (
              <ActivityItem key={entry.id} entry={entry} targets={targets} context={context} />
            ))}
          </ol>
          <div className="act-more">
            {query.hasNextPage ? (
              <button
                type="button"
                className="btn btn-sm"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage ? strings.common.loading : a.loadMore}
              </button>
            ) : (
              <span className="text-fg-muted">{a.noMore}</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** The details pane's Recent activity of one secret (spec 6.10.8): the last 5 entries and Show all. */
export function RecentActivity({
  deploymentId,
  secretName,
  showAllHref,
}: {
  deploymentId: string;
  secretName: string;
  showAllHref: string;
}) {
  const query = useActivity(deploymentId, { secretName }, 5);
  const entries = query.data?.pages[0]?.items ?? [];
  return (
    <div data-testid="recent-activity">
      {query.isPending ? (
        <p className="text-fg-muted">{strings.common.loading}</p>
      ) : entries.length === 0 ? (
        <p className="text-fg-muted">{a.empty}</p>
      ) : (
        <ol className="act-list is-compact" aria-label={strings.variables.details.recentActivity}>
          {foldRevealRequests(entries).map((entry) => (
            <ActivityItem key={entry.id} entry={entry} />
          ))}
        </ol>
      )}
      <Link className="link mt-1 inline-block text-[12px]" to={showAllHref}>
        {strings.variables.details.showAllActivity}
      </Link>
    </div>
  );
}
